import { useState, FormEvent } from 'react';
import './FlightSearch.css';

export interface SearchCriteria {
  aircraftType: string;
  blockTimeMin: number;
  blockTimeMax: number;
  region?: string;
}

interface FlightSearchProps {
  onSearch: (criteria: SearchCriteria) => void;
  isLoading?: boolean;
}

const AIRCRAFT_TYPES = [
  { value: 'A320', label: 'Airbus A320' },
  { value: 'A321', label: 'Airbus A321' },
  { value: 'B737', label: 'Boeing 737' },
  { value: 'B738', label: 'Boeing 737-800' },
  { value: 'B739', label: 'Boeing 737-900' },
  { value: 'B77W', label: 'Boeing 777-300ER' },
  { value: 'B78X', label: 'Boeing 787-10' },
  { value: 'A359', label: 'Airbus A350-900' },
  { value: 'CRJ9', label: 'Bombardier CRJ-900' },
  { value: 'E75L', label: 'Embraer E175' },
];

const REGIONS = [
  { value: '', label: 'All Regions' },
  { value: 'US-WEST', label: 'US West' },
  { value: 'US-EAST', label: 'US East' },
  { value: 'EUROPE', label: 'Europe' },
  { value: 'ASIA', label: 'Asia' },
  { value: 'OCEANIA', label: 'Oceania' },
];

export default function FlightSearch({ onSearch, isLoading = false }: FlightSearchProps) {
  const [aircraftType, setAircraftType] = useState('A320');
  const [blockTimeMin, setBlockTimeMin] = useState(60);
  const [blockTimeMax, setBlockTimeMax] = useState(120);
  const [region, setRegion] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!aircraftType) {
      newErrors.aircraftType = 'Aircraft type is required';
    }

    if (blockTimeMin < 15) {
      newErrors.blockTimeMin = 'Minimum block time must be at least 15 minutes';
    }

    if (blockTimeMax > 600) {
      newErrors.blockTimeMax = 'Maximum block time cannot exceed 600 minutes';
    }

    if (blockTimeMin >= blockTimeMax) {
      newErrors.blockTimeMax = 'Maximum block time must be greater than minimum';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }

    const criteria: SearchCriteria = {
      aircraftType,
      blockTimeMin,
      blockTimeMax,
      region: region || undefined,
    };

    onSearch(criteria);
  };

  return (
    <div className="flight-search">
      <h2>Find Your Flight</h2>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="aircraftType">Aircraft Type</label>
          <select
            id="aircraftType"
            value={aircraftType}
            onChange={(e) => setAircraftType(e.target.value)}
            disabled={isLoading}
          >
            {AIRCRAFT_TYPES.map((aircraft) => (
              <option key={aircraft.value} value={aircraft.value}>
                {aircraft.label}
              </option>
            ))}
          </select>
          {errors.aircraftType && <span className="error">{errors.aircraftType}</span>}
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="blockTimeMin">Min Block Time (minutes)</label>
            <input
              id="blockTimeMin"
              type="number"
              value={blockTimeMin}
              onChange={(e) => setBlockTimeMin(Number(e.target.value))}
              min={15}
              max={600}
              disabled={isLoading}
            />
            {errors.blockTimeMin && <span className="error">{errors.blockTimeMin}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="blockTimeMax">Max Block Time (minutes)</label>
            <input
              id="blockTimeMax"
              type="number"
              value={blockTimeMax}
              onChange={(e) => setBlockTimeMax(Number(e.target.value))}
              min={15}
              max={600}
              disabled={isLoading}
            />
            {errors.blockTimeMax && <span className="error">{errors.blockTimeMax}</span>}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="region">Region (Optional)</label>
          <select
            id="region"
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            disabled={isLoading}
          >
            {REGIONS.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </div>

        <button type="submit" disabled={isLoading} className="search-button">
          {isLoading ? 'Searching...' : 'Search Flights'}
        </button>
      </form>
    </div>
  );
}
